import { faro, LogLevel as FaroLogLevel } from '@grafana/faro-web-sdk'

// structured logging for the overlay. every log goes to the console and, once faro is up, to grafana with its
// context and attributes as separate fields so dashboards can filter on them instead of parsing message strings.
//
// logContext values match plc-app's LogContext (src/lib/logger/logger.ts) where the area is shared, so existing
// grafana queries like logContext="Socket:" pick up the overlay too, split by service_name="plc-twitch-embed"
export enum LogContext {
  OVERLAY = 'Overlay:',
  BETS = 'Bets:',
  SOCKET = 'Socket:',
  PHEONIX_SOCKET = 'Pheonix:',
  APOLLO_CORE = 'Apollo (Core):',
  APOLLO_CMS = 'Apollo (CMS):',
  I18N = 'I18n:',
  REACT = 'React:',
}

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

// attribute values are flattened to strings because that's what faro log context and event attributes accept
export type LogAttributes = Record<string, string | number | boolean | null | undefined>

const LOG_LEVEL_PARAM = 'logLevel'

const LEVEL_BY_NAME: Record<string, LogLevel> = {
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR,
}

// debug is noisy (every graphql operation, every channel join) so it's opt in per overlay url with ?logLevel=debug
const readLogLevel = (): LogLevel => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(LOG_LEVEL_PARAM)?.toLowerCase()
    if (fromUrl && fromUrl in LEVEL_BY_NAME) return LEVEL_BY_NAME[fromUrl]
  } catch {
    // fall through to the default
  }
  return process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG
}

const minimumLevel = readLogLevel()

const FARO_LEVEL: Record<LogLevel, FaroLogLevel> = {
  [LogLevel.DEBUG]: FaroLogLevel.DEBUG,
  [LogLevel.INFO]: FaroLogLevel.INFO,
  [LogLevel.WARN]: FaroLogLevel.WARN,
  [LogLevel.ERROR]: FaroLogLevel.ERROR,
}

/* eslint-disable no-console */
const CONSOLE: Record<LogLevel, (...args: unknown[]) => void> = {
  [LogLevel.DEBUG]: console.debug,
  [LogLevel.INFO]: console.info,
  [LogLevel.WARN]: console.warn,
  [LogLevel.ERROR]: console.error,
}
/* eslint-enable no-console */

const stringifyValue = (value: unknown): string => {
  if (value instanceof Error) return value.message
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

const toFaroAttributes = (attributes: LogAttributes = {}): Record<string, string> =>
  Object.fromEntries(
    Object.entries(attributes)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, stringifyValue(value)]),
  )

const write = (level: LogLevel, context: LogContext, message: string, attributes?: LogAttributes, error?: unknown) => {
  if (level < minimumLevel) return

  CONSOLE[level](context, message, ...(attributes ? [attributes] : []), ...(error ? [error] : []))

  const faroAttributes = toFaroAttributes(attributes)
  if (error) faroAttributes.error = stringifyValue(error)

  faro.api?.pushLog([`${context} ${message}`], {
    level: FARO_LEVEL[level],
    context: { logContext: context, ...faroAttributes },
  })
}

export const logger = {
  debug: (context: LogContext, message: string, attributes?: LogAttributes) =>
    write(LogLevel.DEBUG, context, message, attributes),
  info: (context: LogContext, message: string, attributes?: LogAttributes) =>
    write(LogLevel.INFO, context, message, attributes),
  warn: (context: LogContext, message: string, attributes?: LogAttributes, error?: unknown) =>
    write(LogLevel.WARN, context, message, attributes, error),
  // errors also go to faro's exceptions view with a stack trace, the log line keeps the context alongside
  error: (context: LogContext, message: string, error?: unknown, attributes?: LogAttributes) => {
    write(LogLevel.ERROR, context, message, attributes, error)
    if (error !== undefined) {
      const exception = error instanceof Error ? error : new Error(stringifyValue(error))
      faro.api?.pushError(exception, { context: { logContext: context, message, ...toFaroAttributes(attributes) } })
    }
  },
}

// usage events for grafana dashboards (bets shown, wins celebrated, reloads), counted rather than read
export const trackEvent = (name: string, attributes?: LogAttributes) => {
  if (minimumLevel <= LogLevel.DEBUG) {
    // eslint-disable-next-line no-console
    console.debug(LogContext.OVERLAY, `event ${name}`, attributes ?? {})
  }
  faro.api?.pushEvent(name, toFaroAttributes(attributes))
}
